
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Upload, Edit2, Trash2, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [formData, setFormData] = useState({
    categoryId: '',
    termId: '',
    academicYear: '',
    monthNumber: '',
    amount: '',
    paymentDate: '',
    paymentMethod: '',
    supplier: '',
    receiptNumber: '',
    description: ''
  });

  const fetchCategories = useCallback(async () => {
    try {
      // This will fetch expense categories from backend
      setCategories([
        { id: '1', name: 'Teacher Salary' },
        { id: '2', name: 'ZIMRA' },
        { id: '3', name: 'NSSA/NASSA' },
        { id: '4', name: 'Trip Expense' },
        { id: '5', name: 'Grocery Purchase' },
        { id: '6', name: 'Uniform Supplier' },
        { id: '7', name: 'Transport' },
        { id: '8', name: 'Food' },
        { id: '9', name: 'Maintenance' },
        { id: '10', name: 'Stationery' },
        { id: '11', name: 'Utilities' },
        { id: '12', name: 'Other' }
      ]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedTerm) params.append('termId', selectedTerm);
      if (selectedCategory && selectedCategory !== 'all') params.append('categoryId', selectedCategory);
      if (selectedMonth && selectedMonth !== 'all') params.append('month', selectedMonth);

      const response = await fetch(`${API}/expenses?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setExpenses(Array.isArray(data) ? data : (Array.isArray(data?.expenses) ? data.expenses : []));
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTerm, selectedCategory, selectedMonth]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowAddForm(false);
        setFormData({
          categoryId: '',
          termId: '',
          academicYear: '',
          monthNumber: '',
          amount: '',
          paymentDate: '',
          paymentMethod: '',
          supplier: '',
          receiptNumber: '',
          description: ''
        });
        fetchExpenses();
      }
    } catch (error) {
      console.error('Error adding expense:', error);
    }
  };

  const handleFileUpload = async (expenseId) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.png,.jpg,.jpeg';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const formDataWithFile = new FormData();
        formDataWithFile.append('file', file);
        
        try {
          const response = await fetch(`${API}/expenses/${expenseId}/upload-receipt`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            body: formDataWithFile
          });

          if (response.ok) {
            fetchExpenses();
          }
        } catch (error) {
          console.error('Error uploading receipt:', error);
        }
      }
    };
    fileInput.click();
  };

  const handleApprove = async (expenseId) => {
    try {
      const response = await fetch(`${API}/expenses/${expenseId}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchExpenses();
      }
    } catch (error) {
      console.error('Error approving expense:', error);
    }
  };

  const handleReverse = async (expenseId) => {
    const reason = prompt('Enter reversal reason:');
    if (reason) {
      try {
        const response = await fetch(`${API}/expenses/${expenseId}/reverse`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ reason })
        });

        if (response.ok) {
          fetchExpenses();
        }
      } catch (error) {
        console.error('Error reversing expense:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Expense Management</h1>
            <p className="text-gray-600 mt-2">Record and track all school expenses</p>
          </div>
          <Button onClick={() => setShowAddForm(!showAddForm)} className="gap-2">
            <Plus size={20} /> Add Expense
          </Button>
        </div>

        {/* Add Expense Form */}
        {showAddForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Record New Expense</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddExpense} className="grid grid-cols-2 gap-4">
                <Select value={formData.categoryId} onValueChange={(value) => setFormData({...formData, categoryId: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  type="number"
                  placeholder="Amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  required
                />

                <Input
                  type="date"
                  placeholder="Payment Date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                  required
                />

                <Select value={formData.monthNumber} onValueChange={(value) => setFormData({...formData, monthNumber: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Month 1</SelectItem>
                    <SelectItem value="2">Month 2</SelectItem>
                    <SelectItem value="3">Month 3</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  type="text"
                  placeholder="Supplier/Payee"
                  value={formData.supplier}
                  onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                />

                <Input
                  type="text"
                  placeholder="Receipt Number"
                  value={formData.receiptNumber}
                  onChange={(e) => setFormData({...formData, receiptNumber: e.target.value})}
                />

                <Input
                  type="text"
                  placeholder="Payment Method"
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                  className="col-span-2"
                />

                <Input
                  type="text"
                  placeholder="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="col-span-2"
                />

                <Button type="submit" className="col-span-2">Record Expense</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              <SelectItem value="1">Month 1</SelectItem>
              <SelectItem value="2">Month 2</SelectItem>
              <SelectItem value="3">Month 3</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Expenses Table */}
        <Card>
          <CardHeader>
            <CardTitle>Expenses List</CardTitle>
            <CardDescription>{expenses.length} expenses recorded</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading expenses...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-2">Date</th>
                      <th className="text-left py-2">Category</th>
                      <th className="text-left py-2">Supplier/Payee</th>
                      <th className="text-left py-2">Amount</th>
                      <th className="text-left py-2">Receipt #</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map(expense => (
                      <tr key={expense.id} className="border-b hover:bg-gray-50">
                        <td className="py-3">{expense.payment_date}</td>
                        <td className="py-3">{expense.category_name}</td>
                        <td className="py-3">{expense.supplier_or_payee}</td>
                        <td className="py-3 font-semibold">${expense.amount}</td>
                        <td className="py-3">{expense.receipt_number || '-'}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            expense.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            expense.status === 'Approved' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {expense.status}
                          </span>
                        </td>
                        <td className="py-3 flex gap-2">
                          <button
                            onClick={() => handleFileUpload(expense.id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Upload Receipt"
                          >
                            <Upload size={18} />
                          </button>
                          {expense.status === 'Pending' && (
                            <button
                              onClick={() => handleApprove(expense.id)}
                              className="text-green-600 hover:text-green-800"
                              title="Approve"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleReverse(expense.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Reverse"
                          >
                            <XCircle size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExpensesPage;
